import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

export const useTournamentDocuments = (tournamentId) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("tournamentDocuments")
        .select("*")
        .eq("tournamentID", tournamentId)
        .eq("isActive", true)
        .is("deletedAt", null)
        .order("created", { ascending: false });

      if (error) throw error;

      setDocuments(data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching tournament documents:", err);
      setError("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const addDocument = async (documentData) => {
    try {
      // Additional validation
      if (!documentData.title || !documentData.title.trim()) {
        throw new Error("Document title is required");
      }

      if (!documentData.url || !documentData.url.trim()) {
        throw new Error("Document URL is required");
      }

      // Validate URL format (basic check)
      try {
        new URL(documentData.url);
      } catch {
        throw new Error("Invalid document URL");
      }

      const { data, error } = await supabase
        .from("tournamentDocuments")
        .insert({
          tournamentID: tournamentId,
          ...documentData,
          isPublic: true, // Always public for tournament documents
          createdBy: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      setDocuments((prev) => [...prev, data]);
      return data;
    } catch (err) {
      console.error("Error adding document:", err);
      throw err;
    }
  };

  const updateDocument = async (documentId, updates) => {
    try {
      // Additional validation
      if (!updates.title || !updates.title.trim()) {
        throw new Error("Document title is required");
      }

      if (!updates.url || !updates.url.trim()) {
        throw new Error("Document URL is required");
      }

      // Validate URL format (basic check)
      try {
        new URL(updates.url);
      } catch {
        throw new Error("Invalid document URL");
      }

      const { data, error } = await supabase
        .from("tournamentDocuments")
        .update({
          ...updates,
          updatedBy: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq("id", documentId)
        .select()
        .single();

      if (error) throw error;

      setDocuments((prev) =>
        prev.map((doc) => (doc.id === documentId ? data : doc))
      );
      return data;
    } catch (err) {
      console.error("Error updating document:", err);
      throw err;
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      const { error } = await supabase
        .from("tournamentDocuments")
        .update({
          deletedAt: new Date().toISOString(),
          deletedBy: (await supabase.auth.getUser()).data.user?.id,
          isActive: false,
        })
        .eq("id", documentId);

      if (error) throw error;

      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    } catch (err) {
      console.error("Error deleting document:", err);
      throw err;
    }
  };

  const getDocumentsByType = (documentType) => {
    return documents.filter((doc) => doc.documentType === documentType);
  };

  const getPublicDocuments = () => {
    return documents.filter((doc) => doc.isPublic);
  };

  useEffect(() => {
    if (tournamentId) {
      fetchDocuments();
    }
  }, [tournamentId]);

  return {
    documents,
    loading,
    error,
    addDocument,
    updateDocument,
    deleteDocument,
    getDocumentsByType,
    getPublicDocuments,
    refreshDocuments: fetchDocuments,
  };
};
